import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { CreatePublishTargetRequest, PublishTarget, WorkspaceCredential } from '@/lib/api';
import {
  buildPublishTargetRequest,
  emptyPublishTargetForm,
  isPublishFormValid,
  PUBLISH_TRIGGERS,
  type PublishFormat,
  type PublishTargetForm,
} from '@/lib/publish';

interface Props {
  open: boolean;
  pending: boolean;
  /** Editing an existing target, or `null`/`undefined` to create a new one. */
  target?: PublishTarget | null;
  /** s3 credentials available in the workspace. */
  credentials: WorkspaceCredential[];
  /** Project target language codes selectable for a per-language subset. */
  targetLanguages: string[];
  workspaceSettingsHref: string;
  onClose: () => void;
  onSubmit: (input: CreatePublishTargetRequest) => Promise<void> | void;
}

function formFromTarget(target: PublishTarget): PublishTargetForm {
  return {
    name: target.name ?? '',
    credentialId: target.credentialId ?? '',
    bucket: target.bucket ?? '',
    region: target.region ?? '',
    prefix: target.prefix ?? '',
    format: target.format ?? 'po',
    langs: target.langs ?? [],
    triggers: target.triggers ?? ['manual'],
    enabled: target.enabled ?? true,
  };
}

export function PublishTargetDialog({
  open,
  pending,
  target,
  credentials,
  targetLanguages,
  workspaceSettingsHref,
  onClose,
  onSubmit,
}: Props) {
  const editing = Boolean(target);
  const [form, setForm] = useState<PublishTargetForm>(emptyPublishTargetForm);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setForm(formFromTarget(target));
    } else {
      const next = emptyPublishTargetForm();
      // Pre-select the only credential to save a click in the common case.
      if (credentials.length === 1) next.credentialId = credentials[0].id;
      setForm(next);
    }
  }, [open, target, credentials]);

  function patch(p: Partial<PublishTargetForm>) {
    setForm((cur) => ({ ...cur, ...p }));
  }

  function toggleLang(code: string) {
    setForm((cur) => ({
      ...cur,
      langs: cur.langs.includes(code)
        ? cur.langs.filter((c) => c !== code)
        : [...cur.langs, code],
    }));
  }

  function toggleTrigger(value: PublishTargetForm['triggers'][number]) {
    setForm((cur) => ({
      ...cur,
      triggers: cur.triggers.includes(value)
        ? cur.triggers.filter((t) => t !== value)
        : [...cur.triggers, value],
    }));
  }

  const noCredentials = credentials.length === 0;
  const canSubmit = !pending && !noCredentials && isPublishFormValid(form);

  function submit() {
    if (canSubmit) void onSubmit(buildPublishTargetRequest(form));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? 'Edit publish target' : 'New publish target'}
      description="An S3 destination for exported translation files. Secrets stay in the workspace credential vault — pick one here."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {pending ? 'Saving…' : editing ? 'Save changes' : 'Create target'}
          </Button>
        </>
      }
    >
      {noCredentials ? (
        <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
          No S3 credentials in this workspace yet. Add one under{' '}
          <a className="font-medium underline" href={workspaceSettingsHref}>
            Workspace → Settings → Credentials
          </a>{' '}
          before creating a publish target.
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pt-name">Name</Label>
              <Input
                id="pt-name"
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="Production bucket"
                autoFocus
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-cred">S3 credential</Label>
              <select
                id="pt-cred"
                value={form.credentialId}
                onChange={(e) => patch({ credentialId: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                <option value="" disabled>
                  Select a credential…
                </option>
                {credentials.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.hint ? ` (${c.hint})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-bucket">Bucket</Label>
              <Input
                id="pt-bucket"
                value={form.bucket}
                onChange={(e) => patch({ bucket: e.target.value })}
                placeholder="my-locales-bucket"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-region">Region</Label>
              <Input
                id="pt-region"
                value={form.region}
                onChange={(e) => patch({ region: e.target.value })}
                placeholder="eu-west-1"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-prefix">
                Key prefix <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="pt-prefix"
                value={form.prefix}
                onChange={(e) => patch({ prefix: e.target.value })}
                placeholder="locales/ or locales/{lang}.po"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-format">Format</Label>
              <select
                id="pt-format"
                value={form.format}
                onChange={(e) => patch({ format: e.target.value as PublishFormat })}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="po">PO (gettext)</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Languages{' '}
              <span className="text-muted-foreground">
                ({form.langs.length === 0 ? 'all target languages' : `${form.langs.length} selected`})
              </span>
            </Label>
            {targetLanguages.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                This project has no target languages yet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {targetLanguages.map((code) => {
                  const active = form.langs.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleLang(code)}
                      className={
                        active
                          ? 'inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary'
                          : 'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-foreground hover:border-primary/40'
                      }
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Leave all unselected to publish every project target language.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Triggers</Label>
            <div className="space-y-1.5">
              {PUBLISH_TRIGGERS.map((t) => {
                const active = form.triggers.includes(t.value);
                return (
                  <label
                    key={t.value}
                    className="flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleTrigger(t.value)}
                      className="mt-0.5 size-4 accent-primary"
                    />
                    <span>
                      <span className="font-medium">{t.label}</span>
                      <span className="block text-xs text-muted-foreground">{t.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="size-4 accent-primary"
            />
            <span className="font-medium">Enabled</span>
            <Badge tone={form.enabled ? 'positive' : 'muted'}>
              {form.enabled ? 'active' : 'paused'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Disabled targets are skipped by automatic triggers.
            </span>
          </label>

          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}
